(() => {
  if (window.CybistAccountOverlay) return;

  let lastTrigger = null;

  const setView = (dialog, name, focusTab) => {
    dialog.querySelectorAll('[data-cybist-account-tab]').forEach((tab) => {
      const selected = tab.dataset.cybistAccountTab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focusTab) tab.focus();
    });
    dialog.querySelectorAll('[data-cybist-account-view]').forEach((view) => {
      view.hidden = view.dataset.cybistAccountView !== name;
    });
  };

  const setEditorExpanded = (dialog, expanded) => {
    dialog.querySelectorAll('[data-cybist-profile-editor-open]').forEach((button) => {
      button.setAttribute('aria-expanded', String(expanded));
    });
  };

  const openProfileEditor = (dialog, focusField = true) => {
    const editor = dialog.querySelector('[data-cybist-profile-editor]');
    if (!editor) return;
    setView(dialog, 'profile', false);
    editor.hidden = false;
    setEditorExpanded(dialog, true);
    editor.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    if (focusField) {
      requestAnimationFrame(() => editor.querySelector('input:not([readonly])')?.focus());
    }
  };

  const closeProfileEditor = (dialog, focusTrigger = true) => {
    const editor = dialog.querySelector('[data-cybist-profile-editor]');
    if (!editor) return;
    editor.hidden = true;
    setEditorExpanded(dialog, false);
    if (focusTrigger) dialog.querySelector('[data-cybist-profile-editor-open]')?.focus();
  };

  const closeOrderDetails = (dialog, focusOpener = false) => {
    dialog.querySelectorAll('[data-cybist-order-detail]').forEach((panel) => {
      const opener = dialog.querySelector('[aria-controls="' + panel.id + '"]');
      panel.hidden = true;
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        opener.textContent = 'View order';
        if (focusOpener) opener.focus();
      }
    });
  };

  const initOrderActions = (dialog) => {
    dialog.querySelectorAll('[data-cybist-order-detail-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const panelId = button.getAttribute('aria-controls');
        const panel = panelId && document.getElementById(panelId);
        if (!panel) return;
        const willOpen = panel.hidden;

        closeOrderDetails(dialog);
        panel.hidden = !willOpen;
        button.setAttribute('aria-expanded', String(willOpen));
        button.textContent = willOpen ? 'Hide order' : 'View order';
        if (willOpen) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    });

    dialog.querySelectorAll('[data-cybist-order-detail-close]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('[data-cybist-order-detail]');
        if (!panel) return;
        const opener = dialog.querySelector('[aria-controls="' + panel.id + '"]');
        panel.hidden = true;
        if (opener) {
          opener.setAttribute('aria-expanded', 'false');
          opener.textContent = 'View order';
          opener.focus();
        }
      });
    });

    const previousToggle = dialog.querySelector('[data-cybist-previous-toggle]');
    const previousExtras = Array.from(dialog.querySelectorAll('[data-cybist-previous-extra]'));
    if (previousToggle && previousExtras.length) {
      previousToggle.hidden = false;
      previousToggle.addEventListener('click', () => {
        const showingAll = previousExtras.some((order) => order.hidden);
        previousExtras.forEach((order) => {
          order.hidden = !showingAll;
        });
        previousToggle.textContent = showingAll ? 'Show recent orders ↑' : 'View all orders →';
      });
    }
  };

  const closeDialog = (dialog) => {
    if (!dialog?.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  };

  const openDialog = (trigger) => {
    const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!dialog) return;
    lastTrigger = trigger;
    setView(dialog, trigger.dataset.cybistAccountView || 'orders', false);
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    document.body.classList.add('cybist-account-overlay-open');
    requestAnimationFrame(() => dialog.querySelector('[aria-selected="true"]')?.focus());
  };

  const submitProfileForm = async (form, dialog) => {
    const saveButton = form.querySelector('[data-cybist-profile-save]');
    const status = form.querySelector('[data-cybist-profile-status]');
    const originalLabel = saveButton?.textContent;

    if (saveButton) {
      saveButton.disabled = true;
      saveButton.textContent = 'Saving…';
    }
    if (status) {
      status.textContent = '';
      status.classList.remove('is-error');
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        redirect: 'manual'
      });

      if (response.type !== 'opaqueredirect' && response.status !== 0 && !response.ok) {
        throw new Error('Address update was rejected');
      }

      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('cybist_account', 'profile');
      returnUrl.searchParams.set('cybist_saved', '1');
      window.location.assign(returnUrl.toString());
    } catch (error) {
      if (status) {
        status.textContent = 'We could not save those details. Please check the fields and try again.';
        status.classList.add('is-error');
      }
      if (saveButton) {
        saveButton.disabled = false;
        saveButton.textContent = originalLabel;
      }
      openProfileEditor(dialog, false);
    }
  };

  const initDialog = (dialog) => {
    if (dialog.dataset.cybistAccountReady === 'true') return;
    dialog.dataset.cybistAccountReady = 'true';

    dialog.querySelectorAll('[data-cybist-account-tab]').forEach((tab) => {
      tab.addEventListener('click', () => setView(dialog, tab.dataset.cybistAccountTab, true));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const tabs = Array.from(dialog.querySelectorAll('[data-cybist-account-tab]'));
        const step = event.key === 'ArrowRight' ? 1 : -1;
        const next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
        setView(dialog, next.dataset.cybistAccountTab, true);
      });
    });

    initOrderActions(dialog);

    dialog.querySelectorAll('[data-cybist-account-login]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) return;

        event.preventDefault();
        const returnTo = `${window.location.pathname}${window.location.search}`;
        const loginUrl = new URL('/customer_authentication/login', window.location.origin);
        loginUrl.searchParams.set('return_to', returnTo);
        window.location.assign(`${loginUrl.pathname}${loginUrl.search}`);
      });
    });

    dialog.querySelectorAll('[data-cybist-profile-editor-open]').forEach((button) => {
      button.addEventListener('click', () => openProfileEditor(dialog));
    });

    dialog.querySelectorAll('[data-cybist-profile-editor-close]').forEach((button) => {
      button.addEventListener('click', () => closeProfileEditor(dialog));
    });

    dialog.querySelectorAll('.cybist-account-overlay__profile-form').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitProfileForm(form, dialog);
      });
    });

    dialog.querySelectorAll('[data-cybist-account-close]').forEach((button) => {
      button.addEventListener('click', () => closeDialog(dialog));
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });

    dialog.addEventListener('close', () => {
      document.body.classList.remove('cybist-account-overlay-open');
      closeProfileEditor(dialog, false);
      closeOrderDetails(dialog);
      lastTrigger?.focus();
      lastTrigger = null;
    });

    dialog.addEventListener('cancel', () => {
      document.body.classList.remove('cybist-account-overlay-open');
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-cybist-account-dialog]').forEach(initDialog);
  };

  const openProfileFromReturn = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('cybist_account') !== 'profile') return;

    const dialog = document.querySelector('[data-cybist-account-dialog]');
    const trigger = dialog && document.querySelector('[data-cybist-account-open][aria-controls="' + dialog.id + '"]');
    if (!dialog || !trigger) return;

    openDialog(trigger);
    setView(dialog, 'profile', false);
    openProfileEditor(dialog, false);

    const status = dialog.querySelector('[data-cybist-profile-status]');
    if (status && url.searchParams.get('cybist_saved') === '1') {
      status.textContent = 'Your contact and delivery details have been saved.';
      status.classList.remove('is-error');
    }

    url.searchParams.delete('cybist_account');
    url.searchParams.delete('cybist_saved');
    window.history.replaceState({}, '', url.toString());
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cybist-account-open]');
    if (!trigger) return;
    event.preventDefault();
    openDialog(trigger);
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  const start = () => {
    init();
    openProfileFromReturn();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.CybistAccountOverlay = { init, openDialog, closeDialog, openProfileEditor, closeProfileEditor };
})();